import * as React from "react";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { m } from "framer-motion";
import {
  SurfaceProvider,
  useElevatedSurfaceClasses,
} from "~/lib/surface-context";
import {
  fluidScaleFadeMotion,
  fluidSprings,
  omitMotionConflictingProps,
  useFluidExitUnmount,
  useFluidOverlayRoot,
} from "~/components/ui/fluid-motion";
import { useShape } from "~/lib/shape-context";
import { cn } from "~/lib/utils";

type PopoverMotionContextValue = {
  actionsRef: React.RefObject<PopoverPrimitive.Root.Actions | null>;
  open: boolean;
};

const PopoverMotionContext =
  React.createContext<PopoverMotionContextValue | null>(null);

function usePopoverMotionContext(): PopoverMotionContextValue {
  const context = React.useContext(PopoverMotionContext);

  if (context) {
    return context;
  }

  return {
    actionsRef: { current: null },
    open: false,
  };
}

function Popover({
  actionsRef,
  defaultOpen = false,
  onOpenChange,
  open: controlledOpen,
  ...props
}: PopoverPrimitive.Root.Props) {
  const overlayRoot = useFluidOverlayRoot<
    PopoverPrimitive.Root.Actions,
    PopoverPrimitive.Root.ChangeEventDetails
  >({
    actionsRef,
    defaultOpen,
    onOpenChange,
    open: controlledOpen,
  });

  const contextValue = React.useMemo<PopoverMotionContextValue>(
    () => ({
      actionsRef: overlayRoot.actionsRef,
      open: overlayRoot.open,
    }),
    [overlayRoot.actionsRef, overlayRoot.open],
  );

  return (
    <PopoverMotionContext.Provider value={contextValue}>
      <PopoverPrimitive.Root
        {...overlayRoot.rootProps}
        data-slot="popover"
        {...props}
      />
    </PopoverMotionContext.Provider>
  );
}

const PopoverTrigger = PopoverPrimitive.Trigger;

function PopoverContent({
  align = "end",
  anchor,
  className,
  collisionAvoidance,
  collisionPadding,
  positionMethod,
  side = "bottom",
  sideOffset = 8,
  sticky,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    | "align"
    | "anchor"
    | "collisionAvoidance"
    | "collisionPadding"
    | "positionMethod"
    | "side"
    | "sideOffset"
    | "sticky"
  >) {
  const { actionsRef, open } = usePopoverMotionContext();
  const surface = useElevatedSurfaceClasses(2, 3);
  const shape = useShape();

  useFluidExitUnmount(open, fluidSprings.moderate, () => {
    actionsRef.current?.unmount();
  });

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        anchor={anchor}
        className="isolate z-50 outline-none"
        collisionAvoidance={collisionAvoidance}
        collisionPadding={collisionPadding}
        positionMethod={positionMethod}
        side={side}
        sideOffset={sideOffset}
        sticky={sticky}
      >
        <SurfaceProvider value={surface.level}>
          <PopoverPrimitive.Popup
            data-slot="popover-content"
            render={(popupProps, state) => (
              <m.div
                {...omitMotionConflictingProps(popupProps)}
                {...fluidScaleFadeMotion(state.open, fluidSprings.moderate)}
                onAnimationComplete={() => {
                  if (!state.open) {
                    actionsRef.current?.unmount();
                  }
                }}
              />
            )}
            className={cn(
              surface.className,
              "quiet-scrollbar z-50 max-h-(--available-height) origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-4xl p-0 text-popover-foreground outline-none",
              shape.container,
              className,
            )}
            {...props}
          />
        </SurfaceProvider>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

const PopoverTitle = React.forwardRef<
  HTMLHeadingElement,
  PopoverPrimitive.Title.Props
>(function PopoverTitle({ className, ...props }, ref) {
  return (
    <PopoverPrimitive.Title
      className={cn("text-meta font-medium text-foreground", className)}
      data-slot="popover-title"
      ref={ref}
      {...props}
    />
  );
});

PopoverTitle.displayName = "PopoverTitle";

const PopoverDescription = React.forwardRef<
  HTMLParagraphElement,
  PopoverPrimitive.Description.Props
>(function PopoverDescription({ className, ...props }, ref) {
  return (
    <PopoverPrimitive.Description
      className={cn("text-meta text-muted-foreground", className)}
      data-slot="popover-description"
      ref={ref}
      {...props}
    />
  );
});

PopoverDescription.displayName = "PopoverDescription";

const PopoverClose = React.forwardRef<
  HTMLButtonElement,
  PopoverPrimitive.Close.Props
>(function PopoverClose({ ...props }, ref) {
  return (
    <PopoverPrimitive.Close data-slot="popover-close" ref={ref} {...props} />
  );
});

PopoverClose.displayName = "PopoverClose";

export {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverTitle,
  PopoverTrigger,
};
